/**
 * PrizeRodeoCard — a rodeo on the Prize Management tab.
 *
 * The card does NOT fetch. Its allocation figures arrive from the page's single
 * batched request, so a COLLAPSED rodeo already shows its locked / potential /
 * not-included totals — which is the first thing an admin opens this page to
 * see. Expanding just reveals the per-event rows we already hold; only the
 * per-event contestant detail is fetched on demand, one level down.
 */

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Calendar, AlertTriangle } from 'lucide-react';
import { PrizeEventRow } from './PrizeEventRow';
import {
  prizeOverviewKeys,
  type RodeoPrizeOverview,
} from '@/api/extensions/usePrizeOverview';
import {
  RodeoMasterPrizeToggles,
  type PrizeToggleField,
} from '@/components/delegate/RodeoMasterPrizeToggles';
import { useApplyRodeoPrizeToggle } from '@/features/prize-management/prizeDisplayToggles';

const money = (n: number) =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export interface PrizeRodeoCardProps {
  rodeo: {
    id: string;
    name: string;
    start_date?: string | null;
    is_published?: boolean;
  };
  /** This rodeo's slice of the page's batched overview. Undefined while loading. */
  overview?: RodeoPrizeOverview;
  isLoading: boolean;
  /**
   * Expansion is CONTROLLED by the page, which persists it across refreshes and
   * route changes. Owning it locally would reset every open row the moment the
   * list remounts.
   */
  expanded: boolean;
  onToggle: () => void;
}

export function PrizeRodeoCard({
  rodeo,
  overview,
  isLoading,
  expanded,
  onToggle,
}: PrizeRodeoCardProps) {
  const queryClient = useQueryClient();
  const { applyRodeoToggle, isSaving } = useApplyRodeoPrizeToggle();

  const data = overview;
  const totals = data?.totals;

  // The master toggles read raw `events` rows. The overview already ships the
  // three toggle columns per event, so we project rather than refetch.
  const toggleEvents = useMemo(
    () =>
      (data?.events ?? []).map((e) => ({
        id: e.event_id,
        display_prize_to_contestants: e.display_prize_to_contestants,
        show_prize_amounts_in_results: e.show_prize_amounts_in_results,
        show_prize_amounts_before_complete: e.show_prize_amounts_before_complete,
      })),
    [data],
  );

  const handleApplyMaster = async (field: PrizeToggleField, target: boolean) => {
    await applyRodeoToggle(toggleEvents, field, target);
    // The bulk write moved every event's toggle columns; refetch so the
    // per-event switches below re-sync from DB truth rather than drifting.
    queryClient.invalidateQueries({ queryKey: prizeOverviewKeys.all });
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="flex items-center gap-3 px-4 py-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
          aria-hidden="true"
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-semibold">{rodeo.name}</span>
            {rodeo.is_published === false && (
              <span className="rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                Draft
              </span>
            )}
            {totals && totals.needs_review_count > 0 && (
              <span className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                {totals.needs_review_count} to review
              </span>
            )}
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {rodeo.start_date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" aria-hidden="true" />
                {new Date(rodeo.start_date).toLocaleDateString()}
              </span>
            )}
            {isLoading && !totals && <span>Loading prize allocation…</span>}
            {totals && (
              <>
                <span className="text-emerald-700 dark:text-emerald-400">
                  {money(totals.locked_amount)} locked
                </span>
                <span className="text-amber-700 dark:text-amber-400">
                  {money(totals.potential_amount)} potential
                </span>
                <span className="text-red-700 dark:text-red-400">
                  {money(totals.excluded_amount)} not included
                </span>
              </>
            )}
          </div>
        </div>
      </button>

      {/* Master toggles — outside the disclosure button so flipping one doesn't
          collapse the card. Shown collapsed too: the figures are already here,
          so there's nothing to wait for. */}
      {toggleEvents.length > 0 && (
        <span className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <RodeoMasterPrizeToggles
            idPrefix={`prize-mgmt-rodeo-${rodeo.id}`}
            events={toggleEvents}
            onApply={handleApplyMaster}
            disabled={isSaving || isLoading}
          />
        </span>
      )}
      </div>

      {expanded && (
        <div className="space-y-2 border-t border-border bg-muted/20 p-3">
          {isLoading && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Loading prize allocation…
            </p>
          )}

          {data && data.events.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              This rodeo has no events yet.
            </p>
          )}

          {data?.events.map((summary) => (
            <PrizeEventRow
              key={summary.event_id}
              summary={summary}
              rodeoId={rodeo.id}
              organizationId={data.rodeo.organization_id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
